import React, { useRef } from "react";
import { Button, Typography } from "@material-ui/core";
import DataTable from "@/components/DataTable";
import { ReactRender } from "@/components/ReactRender";
import { useTranslation } from "@/langs/useTranslation";
import {
  getDeviceAlarmListApi,
  resetAlarmDeviceApi,
  unbindDevicesTemplatesApi
} from "@/apis/apiCollection";
import ChipStatus from "@/components/ChipStatus";
import modalConfirm from "@/components/Modal/modalConfirm";
import { toast } from "@/apis/request";
import HasPermission from "@/components/commons/HasPermission";
import { formatDate } from "./datetimeHandlers";
import { renderAlarmPolicy } from "./utils";

export const DeviceAlarmListDatatable = React.memo(
  ({ deviceId, className, style, footer, children, onCheckAll, onLoaded }) => {
    const { t } = useTranslation();
    const tableRef = useRef(null);

    const handleReloadDataTable = () => {
      tableRef.current?.reload();
    };

    const doReset = async rows => {
      if (!Array.isArray(rows)) {
        rows = [rows];
      }
      await resetAlarmDeviceApi({
        resetDevices: rows.map(row => ({
          deviceId,
          alarmPolicyId: row.alarmPolicyId
        }))
      });
      handleReloadDataTable();
      toast.success(t({ defaultMessage: "操作成功" }));
    };

    const doDelete = async rows => {
      if (!Array.isArray(rows)) {
        rows = [rows];
      }
      await unbindDevicesTemplatesApi({
        device_ids: [deviceId],
        alarm_template_ids: rows.map(row => row.alarmTemplateId)
      });
      handleReloadDataTable();
      toast.success(t({ defaultMessage: "删除成功" }));
    };

    const handleAlarmReset = row => {
      modalConfirm({
        title: t({ defaultMessage: "重置提醒" }),
        content: t(
          {
            defaultMessage:
              "是否确认重置（{name}）该报警规则？重置后报警规则将继续执行监控。"
          },
          { name: row.alarmPolicyOptionName }
        ),
        onConfirm: () => {
          return doReset(row);
        }
      });
    };

    const handleAlarmDelete = row => {
      modalConfirm({
        title: t({ defaultMessage: "删除提醒" }),
        content: t(
          {
            defaultMessage: "是否确认删除（{name}）当前报警规则？"
          },
          { name: row.alarmPolicyOptionName }
        ),
        onConfirm: () => {
          return doDelete(row);
        }
      });
    };

    const columns = [
      {
        name: "alarmTemplateName",
        data: "alarmTemplateName",
        title: `${t({ defaultMessage: "报警模板名称" })}`,
        orderable: true,
        searchable: true,
        render: data => {
          return data
            ? data.replaceAll(">", "&gt;").replaceAll("<", "&lt;")
            : data;
        }
      },
      {
        data: "alarmPolicyOptionName",
        orderable: true,
        title: `${t({ defaultMessage: "报警规则" })}`,
        render: (data, type, row) => {
          return renderAlarmPolicy(data, row);
        }
      },
      {
        data: "publishStatus",
        title: t({ defaultMessage: "部署状态" }),
        searchable: true,
        createdCell: (td, data) => {
          ReactRender(
            <>
              {data ? (
                <ChipStatus
                  size="small"
                  type={
                    data === "success"
                      ? "primary"
                      : data === "failed"
                      ? "error"
                      : "default"
                  }
                  label={
                    data === "success"
                      ? t({ defaultMessage: "部署成功" })
                      : data === "failed"
                      ? t({ defaultMessage: "部署失败" })
                      : t({ defaultMessage: "部署中" })
                  }
                />
              ) : (
                "-"
              )}
            </>,
            td
          );
        }
      },
      {
        data: "publishDate",
        title: t({ defaultMessage: "部署日期" }),
        searchable: false,
        render: data => {
          return formatDate(data, "YYYY/MM/DD HH:mm");
        }
      },
      {
        data: "triggerNum",
        title: t({ defaultMessage: "触发状态" }),
        searchable: true,
        createdCell: (td, data) => {
          ReactRender(
            <ChipStatus
              size="small"
              type={data > 0 ? "info" : "warn"}
              label={
                data > 0
                  ? t({ defaultMessage: "已触发" })
                  : t({ defaultMessage: "未触发" })
              }
            />,
            td
          );
        }
      },
      {
        data: "triggerValue",
        title: t({ defaultMessage: "触发值" }),
        searchable: true,
        className: "center",
        createdCell: (td, data) => {
          ReactRender(
            <Typography variant="body2" color="primary">
              {data}
            </Typography>,
            td
          );
        }
      },
      {
        data: "triggerNum",
        title: t({ defaultMessage: "触发次数" }),
        searchable: true,
        className: "center",
        createdCell: (td, data) => {
          ReactRender(
            <Typography variant="body2" color="primary">
              {data}
            </Typography>,
            td
          );
        }
      },
      {
        data: "triggerDate",
        title: t({ defaultMessage: "最后触发时间" }),
        searchable: false,
        render: data => {
          return formatDate(data, "YYYY/MM/DD HH:mm");
        }
      },
      {
        data: "createUserName",
        searchable: true,
        title: t({ defaultMessage: "部署人" })
      },
      {
        data: null,
        title: t({ defaultMessage: "操作" }),
        // className: "operation2",
        searchable: false,
        createdCell: (td, data, row) => {
          ReactRender(
            <HasPermission permission="device_detail_alarm_reset">
              <Button
                size="small"
                color="primary"
                onClick={() => handleAlarmReset(row)}
              >
                {t({ defaultMessage: "重置" })}
              </Button>
            </HasPermission>,
            td
          );
        }
      }
      // {
      //   name: "alarmTemplateName",
      //   data: "alarmTemplateName",
      //   className: "sticky right operation",
      //   searchable: false,
      //   createdCell: (td, data, row) => {
      //     ReactRender(
      //       <HasPermission permission="device_detail_alarm_reset">
      //         <Button
      //           size="small"
      //           color="secondary"
      //           onClick={() => handleAlarmDelete(row)}
      //         >
      //           {t({ defaultMessage: "删除" })}
      //         </Button>
      //       </HasPermission>,
      //       td
      //     );
      //   }
      // }
    ];

    const ajax = (data, callback, settings) => {
      return getDeviceAlarmListApi({
        deviceId,
        keywords: data.search?.value ?? "",
        page: data.page,
        pageSize: data.pageSize
      }).then(res => {
        callback({
          data: res.deviceAlarms,
          draw: data.draw,
          recordsFiltered: res.deviceAlarms.length,
          recordsTotal: res.deviceAlarms.length
        });
      });
    };

    return (
      <DataTable
        ref={tableRef}
        columns={columns}
        // searching
        rowsGroup={["alarmTemplateName:name"]}
        ajax={ajax}
        className={className}
        showFooter={false}
        style={style}
        footer={footer}
        onCheckAll={onCheckAll}
        onLoaded={onLoaded}
        autoLoad
      >
        {children}
      </DataTable>
    );
  }
);
